import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import Decimal from 'decimal.js';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { NUMBER_MIN_VALUE } from 'src/app/shared/constants/constants';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { ICustomDrug } from 'src/app/shared/interfaces/custom-drug-interface';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { FieldType } from 'src/app/shared/interfaces/field-type.enum';
import { GenericTableColumn } from 'src/app/shared/interfaces/genetic-table-column';
import { IInventoryModel } from 'src/app/shared/interfaces/IInventoryModel';
import { ILocationToExclude } from 'src/app/shared/interfaces/ILocationToExclude.interface';
import { ILocationChosenEvent } from 'src/app/shared/interfaces/location-chosen-event.interface';
import { RowType } from 'src/app/shared/interfaces/raw-type.enum';
import { IViolatingTransactionDateNdc } from 'src/app/shared/interfaces/violating-transaction-date-ndc.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { GetInventoryForDrug, ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { IDestroyIngredientSubmittedEvent } from '../../interfaces/destroy-ingredient-submitted-event.interface';
import { IReceiveCompoundFormRow } from '../../interfaces/receive-compound-form-row';
import { IReceiveCompoundDialogSubmittedEvent, IReceiveCompoundWaste } from '../../interfaces/receive-compound-submitted-event.interface';
import { IWasteIngredientInput } from '../../interfaces/waste-ingredient-input.interface';
import { GetIngredientsFromTheLatestCompounding } from 'src/app/shared/states/transactions/transactions.actions';
import { TransactionsState } from 'src/app/shared/states/transactions/transactions.state';

type IReceiveCompoundRowControl = FormControls<IReceiveCompoundFormRow>;

@UntilDestroy()
@Component({
  selector: 'app-receive-compound-dialog',
  templateUrl: './receive-compound-dialog.component.html',
  styleUrls: ['./receive-compound-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InventoryLocationsService, BackCountService]

})
export class ReceiveCompoundDialogComponent implements OnInit {
  
  @Output() save: EventEmitter<IReceiveCompoundDialogSubmittedEvent> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  addCompoundNdcMode: boolean;
  addIngredientMode: boolean;
  wasteIngredientDialog: boolean;

  form: FormGroup;
  compoundFromArray: FormArray<FormGroup<IReceiveCompoundRowControl>> = new FormArray<FormGroup<IReceiveCompoundRowControl>>([]);
  ingredientsFormArray: FormArray<FormGroup<IReceiveCompoundRowControl>> = new FormArray<FormGroup<IReceiveCompoundRowControl>>([]);

  compoundColumns: GenericTableColumn<IReceiveCompoundFormRow>[] = [];
  ingredientColumns: GenericTableColumn<IReceiveCompoundFormRow>[] = [];

  wasteDialogInputData$: BehaviorSubject<IWasteIngredientInput> = new BehaviorSubject(null);
  wastedIngredients = new Array<IDestroyIngredientSubmittedEvent>();

  ActivityTypesEnum = ActivityTypesEnum;
  backCountForReceiveEnabled: boolean;

  @ViewChild('ingredientsTable') ingredientsTable;
  @ViewChild('dialog') dialog;

  dmgExpLocationId = this.store.selectSnapshot(InventoryState.damagedExpiredLocationID);

  /**
   *
   */
  constructor(
    private fb: FormBuilder,
    private store: Store, 
    private formattedDatePipe: FormattedDatePipe, 
    private transactionsService: TransactionsService, 
    private cdr: ChangeDetectorRef,
    private inventoryLocationsService: InventoryLocationsService,
    private messageService: AlertMessageService,
    private backCountService: BackCountService
  ) {
    this.form = fb.group({
      lot: ['', Validators.required],
      expirationDate: ['', Validators.required],
      transactionDate: [new Date(), Validators.required],
      transactionDateDisplayValue: this.formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy'),
    });

    this.backCountForReceiveEnabled = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Received, DrugScheduleEnum.Compound)
    this.initCompoundColumns();
    this.initIngredientColumns();
  }

  
  ngOnInit(): void {
    
  }

  compoundSelected(ndc: ICustomDrug): void {
    if (!ndc) return;
    this.store.dispatch(new GetInventoryForDrug(ndc.id, this.store.selectSnapshot(InventoryState.primaryLocationID)))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      const inventory = this.store.selectSnapshot(InventoryState)?.receiveSelectedDrugInventory as IInventoryModel;
      const group = this.fb.group({
        drugID: ndc?.id,
        friendlyName: ndc?.friendlyName,
        packageNdc11Digits: ndc?.packageNdc,
        locationID: inventory?.locationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID),
        starting: inventory?.onHand ?? 0,
        count: ["", [Validators.required, Validators.min(NUMBER_MIN_VALUE)]],
        isBackCountMatches: [null, this.backCountForReceiveEnabled ? Validators.required : null],
        onHand: inventory?.onHand ?? 0,
        packageSize: ndc.packageSize,
        note: null,
       }) as FormGroup<IReceiveCompoundRowControl>;

       group.get('count').valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(x => {
        const starting = group.get('starting').value;
        group.get('onHand').setValue(new Decimal(+starting).add(new Decimal(+x)).toNumber());
      });

       const newFormArray = new FormArray([...this.compoundFromArray.controls, group]);
       this.compoundFromArray = newFormArray;
   
       this.addCompoundNdcMode = false;

       this.prefillCompoundWithIngredients(ndc.id);

       this.cdr.markForCheck();
    });

    
  }

  ingredientSelected(ndc: IInventoryModel): void {
    this.inventoryLocationsService.addLocationsWithPositiveInventory(ndc.drugID, ndc?.positiveInventoryBalanceLocationIDs);
   
    const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Mixed, ndc.schedule, ndc.drugID);

    const group = this.fb.group({
      drugID: ndc?.drugID,
      friendlyName: ndc?.friendlyName,
      packageNdc11Digits: ndc?.packageNdc11Digits,
      locationID: ndc?.locationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID),
      locationName: ndc?.locationName,
      starting: ndc.onHand,
      count: ["", [Validators.required, Validators.max(ndc.onHand), Validators.min(NUMBER_MIN_VALUE)]],
      onHand: ndc.onHand,
      isBackCountMatches: [null, backCountEnabledForTheActivity ? Validators.required : null],
      note: null,
      hasWastedIngredients: null
     }) as FormGroup<IReceiveCompoundRowControl>;

     group.get('count').valueChanges
     .pipe(untilDestroyed(this), distinctUntilChanged())
     .subscribe(x => {
       const starting = group.get('starting').value;
       group.get('onHand').setValue(new Decimal(+starting).sub(new Decimal(+x)).toNumber());
      });
      // Change FormArray reference to trigger change detection in child
     const newFormArray = new FormArray([...this.ingredientsFormArray.controls, group]);
     this.ingredientsFormArray = newFormArray;
     this.addIngredientMode = false;
  }

  handleIngredientDeletedSideEffect(row): void {
    this.backCountService.removeDrugIDRequiringBackCount(row.drugID);
  }

  submitForm(): void {
    if(!this.ingredientsFormArray?.length) {
      this.raiseEmptyCompoundError();
      return;
    }
    if (this.form.invalid || this.compoundFromArray.invalid || this.ingredientsFormArray.invalid) {
      highlightRequiredControlsWithErrors(this.form);
      highlightRequiredControlsWithErrors(this.compoundFromArray.controls[0]);
      this.ingredientsFormArray.controls?.forEach(group => highlightRequiredControlsWithErrors(group));
      return;
    }
    
    const payload = {
      transactionDate: this.form.get('transactionDate').value,
      inventoryActions: this.ingredientsFormArray?.getRawValue()?.map(x => {
       // const relatedWasteQty = +this.wastedIngredients.find(wasted => wasted.locationID === x.locationID && wasted.drugID === x.drugID)?.addedQuantity ?? 0;
        return (
          {
            drugId: x.drugID,
            //can combine mix and waste into a transaction for validation
           // addedQuantity: +x.count + (relatedWasteQty || 0),
            addedQuantity: +x.count,
            activityType: ActivityTypesEnum.Dispensed,
            locationId : x.locationID
        })
      })
    }
    this.store.dispatch(new ValidateTransactionDateForInventories(payload))
    .pipe(untilDestroyed(this))
    .subscribe(resp => {
      const validateTrDateResponse = resp.inventory?.response;
      if (
        validateTrDateResponse?.success &&
        validateTrDateResponse?.value?.length
      ) {
        this.markConflictingNdcs(validateTrDateResponse.value?.map((x) => ({ drugId: x.drugId, locationId: x.locationId})));
        this.transactionsService.handleTransactionDateErrors(validateTrDateResponse.value);
      } else {
        const formValue = this.form.getRawValue();
        const compoundFormValue = this.compoundFromArray.getRawValue()[0];
        const ingredientsFormValue = this.ingredientsFormArray.getRawValue();
        const waste: IReceiveCompoundWaste[] = [];
        const ingredients = ingredientsFormValue.map(ingredient => {
          let wastedIngredient = this.wastedIngredients.find(x => x.locationID === ingredient.locationID && x.drugID === ingredient.drugID);
          if (wastedIngredient) {
            waste.push({
              nameOfDestruction: wastedIngredient.nameOfDestruction,
              methodOfDestruction: wastedIngredient.methodOfDestruction,
              placeOfDestructionId: wastedIngredient.placeOfDestructionId,
              transactionDate: wastedIngredient.transactionDate,
              drugId: wastedIngredient.drugID,
              locationId: wastedIngredient.locationID,
              addedQty: wastedIngredient.addedQuantity,
              note: wastedIngredient.note,
              pin: wastedIngredient.pin,
              witnessPin: wastedIngredient.witnessPin
            });
          }
          return ({
            drugId: ingredient.drugID,
            locationId: ingredient.locationID,
            addedQty: +ingredient.count,
            note: ingredient.note,
            isBackCountMatches: ingredient.isBackCountMatches
          });
        });
        
        this.save.next({
          compoundId: compoundFormValue.drugID,
          lot: formValue.lot,
          expirationDate: formValue.expirationDate,
          transactionDate: formValue.transactionDate,
          compoundLocationId: compoundFormValue.locationID,
          compoundQTY: +compoundFormValue.count,
          isCompoundBackCountMatches: compoundFormValue.isBackCountMatches,
          note: compoundFormValue.note,
          ingredients,
          waste 
        });
        
      }
    })
  }

  openWasteDialog(row: IReceiveCompoundFormRow): void {
   this.wasteDialogInputData$.next(
    {
      ...row, 
      locationName: this.store.selectSnapshot(InventoryState.locations)?.find(x => x.id === row.locationID)?.name ?? '',
      nameOfDestruction: `Waste for ${this.compoundFromArray.controls[0].get('friendlyName').value} ${this.form.get('lot').value ? '| ' + this.form.get('lot').value : ''}`
    });
    this.wasteIngredientDialog = true;
  }

  ingredientDestroyed(data: IDestroyIngredientSubmittedEvent): void {
    const index = this.wastedIngredients.findIndex(x => x.locationID === data.locationID && x.drugID == data.drugID);
    //update or insert
    if (index > -1) {
      this.wastedIngredients.splice(index, 1, data);
    } else {
      this.wastedIngredients.push(data);
    }
    this.updateIngredientValidators(data.locationID, data.drugID, new Decimal(+data.addedQuantity).toNumber())
    //calc value once
    this.getIngredientsFormRow(data.locationID, data.drugID).get('hasWastedIngredients').patchValue(true);
    this.wasteIngredientDialog = false;
  }


  getLocationsToExclude(): ILocationToExclude[] {
    return this.ingredientsFormArray.getRawValue()
    .map(x => ({id: x.locationID, drugID: x.drugID}));
   }

  handleCompoundTabbing(): void {
    if (!this.ingredientsFormArray?.length) {
      this.dialog.focusCancelButton();
    } else {
      this.ingredientsTable?.focusInput(0);
    } 
  }

  handleIngredientsTabbing(): void {
    if (this.ingredientsFormArray.invalid || this.form.invalid || this.compoundFromArray.invalid) {
      this.dialog.focusCancelButton();
    } else {
      this.dialog.focusSubmitButton();
    }
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }

  removeWaste(row: IReceiveCompoundFormRow): void {
   const index = this.wastedIngredients.findIndex(x => x.locationID === row.locationID && x.drugID == row.drugID);
   if (index > -1) {
    this.wastedIngredients.splice(index, 1);
    this.getIngredientsFormRow(row.locationID, row.drugID).get('hasWastedIngredients').patchValue(false);
    this.updateIngredientValidators(row.locationID, row.drugID);
   }
  }

  // adjust waste button
  ingredientLocationChosen(ev: ILocationChosenEvent): void {
    const group = this.ingredientsFormArray.at(ev.rowN);
    const hasWastedIngredientsControl = group.get('hasWastedIngredients');
    const locationId = group.get('locationID')?.value;
    const drugId = group.get('drugID')?.value;
    const relatedWasteIdx = this.wastedIngredients.findIndex(x => x.locationID === locationId && x.drugID === drugId);
    if (relatedWasteIdx > -1) {
      setTimeout(() => {
        this.updateIngredientValidators(locationId, drugId, this.wastedIngredients[relatedWasteIdx].addedQuantity);
      });
      hasWastedIngredientsControl.patchValue(true);
    } else {
      hasWastedIngredientsControl.patchValue(false);
    }
  }

  private getIngredientsFormRow(locationID: string, drugID: string): FormGroup<FormControls<IReceiveCompoundFormRow>> {
    return this.ingredientsFormArray.controls.find(control => {
      const location = control.get('locationID').value;
      const drug = control.get('drugID').value;
      if (locationID === location && drugID === drug) {
        return control;
      }
    });
  }

  /**
 * Updates Ingredient input min/max value taking into account already wasted amount
 * Use when waste submitted/removed or location changed
 */
  private updateIngredientValidators(locationID: string, drugID: string, wasteQty: number = 0): void {
    const ingredientGroup = this.getIngredientsFormRow(locationID, drugID);
    const ingredientNewMaxValue = new Decimal(+ingredientGroup.get('starting').value).sub(wasteQty).toNumber();
    ingredientGroup.get('count').setValidators([Validators.required, Validators.max(ingredientNewMaxValue), Validators.min(NUMBER_MIN_VALUE)]);
    ingredientGroup.get('count').updateValueAndValidity();
  }

  private raiseEmptyCompoundError(): void {
    this.messageService.alertMessage$.next({
      content: 'Cannot receive a compound with no ingredients.',
       type: 'error-info', 
       autoHide: 3, 
       closeButtonName: null, 
       width: 'fit-content'
      });
  }

 markConflictingNdcs(conflictingNDCs: IViolatingTransactionDateNdc[]): void {
  this.ingredientsFormArray
  conflictingNDCs.forEach(ndc => {
    const drugControl = this.ingredientsFormArray.controls.find(control => control.value.drugID === ndc.drugId && control.value.locationID === ndc.locationId);
    drugControl?.get('count')?.setErrors({causedNegativeBalance: true});
  });
  this.form.get('transactionDateDisplayValue')?.setErrors({causedNegativeBalance: true});
  this.ingredientsTable?.markForCheck();
 }

  now(): Date {
    return new Date();
  }

  private prefillCompoundWithIngredients(drugID: string): void {
    this.store.dispatch(new GetIngredientsFromTheLatestCompounding(drugID))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      const ingredients = this.store.selectSnapshot(TransactionsState.currentIngredientsFromTheLatestCompounding);
      if(ingredients?.length) {
        ingredients.forEach(x => this.ingredientSelected(x));
        this.cdr.markForCheck();
      }
    });
  }


  private initCompoundColumns(): void {
    this.compoundColumns = [
      new GenericTableColumn<IReceiveCompoundFormRow>({ header: 'Drug', field: 'friendlyName', RowType: RowType.Double,
        doubleRowField: {
          headingField: 'friendlyName', 
          secondaryField1: 'packageNdc11Digits'
        },
        style: {width: '19%'},
        class: '!pl-4',
        sortable: false,
      }),
      new GenericTableColumn<IReceiveCompoundFormRow>({
        header: 'Package Size',
        field: 'packageSize',
        fieldType: FieldType.Number,
        sortable: false,
        style: {width: '140px'}
      }),
    ];

      if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        this.compoundColumns.push(
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'Into Location',
            field: 'locationID',
            RowType: RowType.Location,
            sortable: false,
            locationRow: {startingField: 'starting', onHandField: 'onHand', countField: 'count', drugIDField: 'drugID'},
            style: {width: '185px'},
            elementStyle: {width: '185px'},
          })
        );
      }

      this.compoundColumns = [
        ...this.compoundColumns,
        ...[
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'Start',
            field: 'starting',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'QTY CMPD',
            field: 'count',
            RowType: RowType.InputNumber,
            sortable: false,
            style: { 'text-align': 'right', width: '117px' },
            tdClass: '!bg-transparent'
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'On-Hand',
            field: 'onHand',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          }),
        ],
      ];

      if (this.backCountForReceiveEnabled) {
        this.compoundColumns.push (new GenericTableColumn<IReceiveCompoundFormRow>({
          header: '',
          RowType: RowType.BackCountNotResolvedTooltip,
          additionalProps: {locationID: 'locationID', drugID: 'drugID'},
          style: {width: '0px'},
          sortable: false,
        }));
        this.compoundColumns.push (
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'BACK-COUNT OK?',
            field: 'isBackCountMatches',
            RowType: RowType.BackCount,
            sortable: false,
            style: {width: '163px'}
          }),
        )
      }

      this.compoundColumns = [
        ...this.compoundColumns,
        ...[
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: '',
            field: 'note',
            sortable: false,
            RowType: RowType.Note,
            noteRow: {noteDrugNameField: 'friendlyName'},
            class: '!pr-1',
            style: {width: '44px'},
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: '',
            sortable: false,
            RowType: RowType.RemoveRowButton,
            style: {width: '56px'},
            class: '!pr-4',
          }),
        ],
      ]; 
  }

  private initIngredientColumns(): void {
    this.ingredientColumns = [
      new GenericTableColumn<IReceiveCompoundFormRow>({ header: 'Drug', field: 'friendlyName', RowType: RowType.Double,
        doubleRowField: {
          headingField: 'friendlyName', 
          secondaryField1: 'packageNdc11Digits'
        },
        style: {width: '30%'},
        class: '!pl-4',
        sortable: false,
      }),
    ];

      if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        this.ingredientColumns.push(
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'From Location',
            field: 'locationID',
            RowType: RowType.Location,
            sortable: false,
            locationRow: {startingField: 'starting', onHandField: 'onHand', countField: 'count', drugIDField: 'drugID'},
            style: {width: '185px'},
            elementStyle: {width: '185px'},
          })
        );
      }

      this.ingredientColumns = [
        ...this.ingredientColumns,
        ...[
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'Start',
            field: 'starting',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'Count',
            field: 'count',
            RowType: RowType.InputNumber,
            sortable: false,
            style: { 'text-align': 'right', width: '117px' },
            tdClass: '!bg-transparent',
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: 'On-Hand',
            field: 'onHand',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          }),
        ],
      ];

      const showBackCountHeaderPredicate = () => this.backCountService.isAnyRequiresBackCount();
      const showBackCountTogglePredicate = (rowValue) => this.backCountService.isDrugRequiresBackCount(rowValue.drugID)

      this.ingredientColumns.push (new GenericTableColumn<IReceiveCompoundFormRow>({
        header: '',
        RowType: RowType.BackCountNotResolvedTooltip,
        additionalProps: {locationID: 'locationID', drugID: 'drugID'},
        style: {width: '0px'},
        sortable: false,
        headerDisplayPredicate: showBackCountHeaderPredicate,
        rowValueDisplayPredicate: showBackCountTogglePredicate
      }));
      this.ingredientColumns.push (
        new GenericTableColumn<IReceiveCompoundFormRow>({
          header: 'BACK-COUNT OK?',
          field: 'isBackCountMatches',
          RowType: RowType.BackCount,
          sortable: false,
          style: {width: '163px'},
          headerDisplayPredicate: showBackCountHeaderPredicate,
          rowValueDisplayPredicate: showBackCountTogglePredicate
        }),
      )

      this.ingredientColumns = [
        ...this.ingredientColumns,
        ...[
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: '',
            sortable: false,
            RowType: RowType.Template,
            style: {width: '130px'},
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: '',
            field: 'note',
            sortable: false,
            RowType: RowType.Note,
            noteRow: {noteDrugNameField: 'friendlyName'},
            class: '!pr-1',
            style: {width: '44px'},
          }),
          new GenericTableColumn<IReceiveCompoundFormRow>({
            header: '',
            sortable: false,
            RowType: RowType.RemoveRowButton,
            style: {width: '56px'},
            class: '!pr-4',
          }),
        ],
      ]; 
  }

}
